import { useState, useRef, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { NavIcon } from '../../../shell/shared';
import { uploadToStorage } from '../../../lib/upload-to-storage';
import { type FileTag, type PatientFileItem } from './consultation-data';

// Tag chips available in the upload drawer
const UPLOAD_TAGS: { label: string; value: FileTag }[] = [
  { label: 'Images', value: 'images' },
  { label: 'Documents', value: 'documents' },
  { label: 'Consent', value: 'consent' },
  { label: 'Lab', value: 'lab' },
  { label: 'Before/After', value: 'before_after' },
  { label: 'Other', value: 'other' },
];

interface UploadDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: PatientFileItem) => void;
  orgId: string;
  patientId: string;
  brandTokens: Record<string, string>;
}

export function UploadDrawer({
  open,
  onOpenChange,
  onUpload,
  orgId,
  patientId,
  brandTokens,
}: UploadDrawerProps) {
  const [selectedTag, setSelectedTag] = useState<FileTag>('other');
  const [description, setDescription] = useState('');
  const [starAfterUpload, setStarAfterUpload] = useState(false);
  const [rawFile, setRawFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!open) return null;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setRawFile(file);
      setError(null);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setRawFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!rawFile) return;
    setUploading(true);
    setError(null);

    const fileId = crypto.randomUUID();
    const isImage = rawFile.type.startsWith('image/');

    try {
      const result = await uploadToStorage({
        orgId,
        patientId,
        file: rawFile,
        fileId,
      });

      const newFile: PatientFileItem = {
        id: fileId,
        patientId,
        kind: isImage ? 'image' : 'document',
        filename: result.filename,
        tag: selectedTag,
        createdAt: new Date().toISOString(),
        starred: starAfterUpload,
        mimeType: result.mimeType,
        description: description || undefined,
        url: result.downloadUrl,
      };
      onUpload(newFile);

      // Reset
      setRawFile(null);
      setDescription('');
      setStarAfterUpload(false);
      setSelectedTag('other');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return;
    setRawFile(null);
    setDescription('');
    setStarAfterUpload(false);
    setError(null);
    onOpenChange(false);
  };

  const primary = brandTokens['--color-primary'] || '#78716c';

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.3)',
          zIndex: 50,
        }}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          maxWidth: '100vw',
          background: 'var(--bg-surface)',
          boxShadow: 'var(--shadow-xl)',
          zIndex: 51,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--sp-5) var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Upload
          </h2>
          <button
            type="button"
            onClick={handleClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <NavIcon name="x" />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: 'var(--sp-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-5)',
          }}
        >
          {/* Drop zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? primary : 'var(--border-input)'}`,
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-8) var(--sp-6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              cursor: 'pointer',
              background: dragOver ? 'rgba(120,113,108,0.04)' : 'var(--bg-surface-2)',
              transition: 'border-color 0.15s, background 0.15s',
            }}
          >
            <div style={{ color: 'var(--text-muted)' }}>
              <NavIcon name="upload" />
            </div>
            {rawFile ? (
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {rawFile.name}
              </span>
            ) : (
              <>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  Drag & drop files here
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  or click to browse
                </span>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </div>

          {/* Tag selection */}
          <div>
            <label style={labelStyle}>Tag</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
              {UPLOAD_TAGS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSelectedTag(t.value)}
                  style={{
                    height: 30,
                    padding: '0 var(--sp-3)',
                    borderRadius: 'var(--radius-full)',
                    border:
                      selectedTag === t.value
                        ? `1.5px solid ${primary}`
                        : '1px solid var(--border-input)',
                    background: selectedTag === t.value ? `${primary}10` : 'var(--bg-surface)',
                    color: selectedTag === t.value ? primary : 'var(--text)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description..."
              rows={3}
              style={{
                width: '100%',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Star toggle */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
            }}
          >
            <div
              onClick={(e) => {
                e.preventDefault();
                setStarAfterUpload(!starAfterUpload);
              }}
              style={{
                width: 40,
                height: 22,
                borderRadius: 11,
                background: starAfterUpload ? primary : 'var(--border-input)',
                position: 'relative',
                transition: 'background 0.2s',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: '#fff',
                  position: 'absolute',
                  top: 2,
                  left: starAfterUpload ? 20 : 2,
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                }}
              />
            </div>
            Star after upload
          </label>
        </div>

        {/* Error */}
        {error && (
          <div
            style={{
              margin: '0 var(--sp-6)',
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fef2f2',
              color: '#dc2626',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-6)',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--sp-3)',
          }}
        >
          <Button variant="outline" size="md" onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="gradient"
            size="md"
            onClick={handleUpload}
            disabled={!rawFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
    </>
  );
}

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semi)',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 'var(--sp-2)',
};
